import React, { useState, useEffect, useRef } from 'react';
import styles from './ClientReviews.module.css';
import ReviewsModal from '../components/ReviewsModal';
import Button from '../components/Button';

interface Review {
  name: string;
  text: string;
  rating: number;
}

const mockReviews: Review[] = [
  {
    name: 'Sarah P.',
    text: 'Absolutely amazing work! The attention to detail and cleanliness was top notch.',
    rating: 5,
  },
  {
    name: 'Daniel M.',
    text: 'Super friendly and professional team. The kitchen backsplash turned out perfect!',
    rating: 4,
  },
  {
    name: 'Laura G.',
    text: 'Flexible scheduling and very easy to work with. Highly recommend!',
    rating: 5,
  },
  {
    name: 'Mark R.',
    text: 'Quick, clean, and professional. The tiling job exceeded our expectations!',
    rating: 5,
  },
  {
    name: 'Chloe S.',
    text: 'The crew was very respectful of our home and delivered beautiful results.',
    rating: 4,
  },
];

const ClientReviews: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [visibleElements, setVisibleElements] = useState<number[]>([]);
  const scrollPositionRef = useRef<number>(0);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Staggered animation for title, 3 review cards, and button
            const totalElements = 5; // title + 3 cards + button
            for (let i = 0; i < totalElements; i++) {
              setTimeout(() => {
                setVisibleElements(prev => {
                  if (!prev.includes(i)) {
                    return [...prev, i];
                  }
                  return prev;
                });
              }, i * 200); // 200ms delay between each element
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleOpenModal = (): void => {
    setModalOpen(true);
    // Save current scroll position
    scrollPositionRef.current = window.scrollY;
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.style.width = '100%';
    // Hide header
    const header = document.querySelector('header');
    if (header) {
      (header as HTMLElement).style.display = 'none';
    }
  };

  const handleCloseModal = (): void => {
    setModalOpen(false);
    // Restore body scroll
    const scrollY = scrollPositionRef.current;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    // Show header
    const header = document.querySelector('header');
    if (header) {
      (header as HTMLElement).style.display = '';
    }
    // Restore scroll position
    window.scrollTo(0, scrollY);
  };

  return (
    <section ref={sectionRef} className={styles.reviewsSection}>
      <h1 className={`${styles.title} ${visibleElements.includes(0) ? styles.fadeInDown : styles.animationHidden}`}>
        What Our Clients Say
      </h1>
      <div className={styles.reviewList}>
        {mockReviews.slice(0, 3).map((review, index) => (
          <div
            key={index}
            className={`${styles.reviewCard} ${visibleElements.includes(index + 1) ? styles.fadeInUp : styles.animationHidden}`}
          >
            <div className={styles.stars}>
              {'★'.repeat(review.rating)}
              {'☆'.repeat(5 - review.rating)}
            </div>
            <p className={styles.reviewText}>"{review.text}"</p>
            <p className={styles.reviewerName}>— {review.name}</p>
          </div>
        ))}
      </div>
      <div className={visibleElements.includes(4) ? styles.fadeInUp : styles.animationHidden}>
        <Button
          onClick={handleOpenModal}
        >
          See All Reviews
        </Button>
      </div>

      <ReviewsModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        reviews={mockReviews}
      />
    </section>
  );
};

export default ClientReviews;